import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Partner, useGetAllPartners } from 'src/services/partner';
import Select from 'src/components/ui/Select/Select';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import AdressForm from 'src/components/layouts/OutboundOverlay/AdressForm/AdressForm';
import { defaultPartner } from 'src/pages/Inbound/InboundFormContext';
import AddressForm from '../../../pages/Inbound/AddressForm/AddressForm';

interface PartnerSelectProps {
  selectedPartner?: Partner;
  onPartnerChange: (partner?: Partner | null, isValid?: boolean) => void;
  className?: string;
  readOnly?: boolean;
  withTaxId?: boolean;
  errors?: any;
  domain?: string;
  parentComponent: 'outbound' | 'inbound';
}

const PartnerSelect: React.FC<PartnerSelectProps> = ({
  selectedPartner,
  onPartnerChange,
  className,
  readOnly,
  withTaxId = true,
  errors,
  domain,
  parentComponent,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | null>(null);
  const [showNewPartnerForm, setShowNewPartnerForm] = useState(false);
  const [, setIsPartnerValid] = useState<boolean>(false);

  const { data } = useGetAllPartners({
    page: 1,
    size: 100,
    domain: search ? `[("name", "ilike", "${search}")]` : domain || undefined,
  });

  const partners = data?.items ?? [];

  const partnerOptions = partners.map(partner => ({
    value: partner?.id?.toString() || '',
    label: [partner.name, partner.company_name, partner.address.city, partner.address.country_name]
      .filter(Boolean)
      .join(', '),
  }));

  const onPartnerInputChange = (inputValue: string) => {
    setSearch(inputValue);
  };

  const triggerNewPartner = (activate: boolean) => {
    if (activate) {
      setShowNewPartnerForm(true);
    } else {
      onPartnerChange(defaultPartner as any);
      setShowNewPartnerForm(false);
    }
  };

  if (readOnly) {
    return (
      <div className="grid grid-cols-[1fr_auto_1fr] gap-x-24 gap-y-16 w-full">
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-2">
            {selectedPartner?.company_name && (
              <span className="typo-text-14 text-faded">{selectedPartner.company_name}</span>
            )}
            {!selectedPartner?.company_name && <span className="typo-text-14 text-faded"> - </span>}
          </div>

          {selectedPartner?.address && (
            <div className="flex flex-col gap-2 text-faded">
              {selectedPartner.address?.street && (
                <span className="typo-text-14 text-faded">{selectedPartner.address.street}</span>
              )}
              {selectedPartner.address?.city && (
                <span className="typo-text-14 text-faded">{selectedPartner.address.city}</span>
              )}
              {selectedPartner.address?.zip_code && (
                <span className="typo-text-14 text-faded">{selectedPartner.address.zip_code}</span>
              )}
              {selectedPartner.address?.country_name && (
                <span className="typo-text-14 text-faded">{selectedPartner.address.country_name}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-16 w-[115px]">
          {selectedPartner?.email && (
            <div className="flex items-center w-[50px]">
              <span className="typo-text-14--bold text-faded">{t('general.email')}</span>
            </div>
          )}
          {selectedPartner?.phone && (
            <div className="flex items-center w-[50px]">
              <span className="typo-text-14--bold text-faded">{t('general.phone')}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-16">
          {selectedPartner?.email && (
            <div className="flex items-center gap-24">
              <span className="typo-text-14 text-faded">{selectedPartner.email}</span>
            </div>
          )}

          {selectedPartner?.phone && (
            <div className="flex items-center gap-24">
              <span className="typo-text-14 text-faded">{selectedPartner.phone}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-16 ${className}`}>
      {!showNewPartnerForm && (
        <div className="flex gap-16">
          <Select
            isSearchable
            placeholder={t('general.type_to_search')}
            onInputChange={onPartnerInputChange}
            className="w-full"
            options={partnerOptions}
            defaultValue={
              selectedPartner && partnerOptions.find(option => option.value === selectedPartner?.id?.toString())
            }
            onChange={option => {
              const partner = partners.find(p => p?.id?.toString() === option?.value);
              onPartnerChange(partner);
            }}
          />
          <Button
            icon="plus"
            onClick={() => triggerNewPartner(true)}
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('general.add_new_partner')}
          />
          <Link
            to="/settings?tab=contacts"
            className="mr-auto self-center whitespace-nowrap typo-text-14--medium text-primary hover:underline">
            {t('contact.manage')}
          </Link>
        </div>
      )}
      {(showNewPartnerForm || selectedPartner?.id) && (
        <>
          {parentComponent === 'outbound' ? (
            <AdressForm
              onChange={partner => onPartnerChange(partner)}
              editable
              partner={selectedPartner as any}
              errors={errors}
              withTaxId={withTaxId}
            />
          ) : (
            <AddressForm
              onChange={(partner, valid) => {
                setIsPartnerValid(valid);
                onPartnerChange(partner, valid);
              }}
              editable
              partner={selectedPartner as any}
              errors={errors}
              withTaxId={withTaxId}
            />
          )}
          <div className="flex">
            <Button
              className="ml-auto"
              onClick={() => triggerNewPartner(false)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerSelect;
